// Parsing + validation for the Ordering (replenishment) tool.
//
// Mirrors the manual Excel process documented in
// "Replenishment Process Instructions": each run an admin uploads three source
// files that today get pasted into tabs of the "Order Template" workbook.
//
//   1. Excess Calc   , AccountEdge -> Analyze Inventory Summary report   (XLSX)
//   2. Stock Status  , ClaimSoft   -> Stock Status report                (CSV)
//   3. Transfer Order, ClaimSoft   -> Transfer Order export              (CSV)
//
// The documented layouts (which row the header sits on, which row data starts,
// which columns are copied) are encoded as constants below so they read like
// the spec and are easy to adjust if an export format changes. We never type-
// coerce values, everything is kept as text so leading zeros on item numbers
// survive (the leading-zero pitfall called out in Step 4 of the doc).

import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { unwrapExcel, decodeCatalogBytes } from './catalogParse';

// --- Documented source layouts -------------------------------------------
// Rows/cols are 1-based here to match how the doc (and Excel) refer to them;
// extractTable() converts to 0-based indexes internally.
//
//   headerRow   , the 1-based row that holds the column headers
//   firstDataRow, the 1-based row the real data starts on
//   firstCol/lastCol, 1-based inclusive column span that gets copied
// Data runs from firstDataRow to the first fully-blank row (see extractTable).

export const LAYOUTS = {
  excess_calc: {
    label: 'Excess Calc',
    origin: 'AccountEdge -> Analyze Inventory Summary',
    kind: 'xlsx',
    headerRow: 10, firstDataRow: 11, firstCol: 2, lastCol: 7, // B to G
    expectedCols: 6,
  },
  stock_status: {
    label: 'Stock Status',
    origin: 'ClaimSoft -> Stock Status',
    kind: 'csv',
    headerRow: 10, firstDataRow: 11, firstCol: 1, lastCol: 22, // A to V
    expectedCols: 22,
  },
  transfer_order: {
    label: 'Transfer Order',
    origin: 'ClaimSoft -> Transfer Order',
    kind: 'csv',
    headerRow: 1, firstDataRow: 2, firstCol: 1, lastCol: 28, // A to AB
    expectedCols: 28,
  },
};

// Find the first header (0-based index) matching one of the given patterns,
// trying them in priority order. Returns -1 if none match.
export function findHeaderIndex(headers, regexes) {
  for (const re of regexes) {
    const i = (headers || []).findIndex(h => re.test((h || '').trim()));
    if (i >= 0) return i;
  }
  return -1;
}

// Locate the ClaimSoft item-number column by header name. Column position
// varies between exports (e.g. the Transfer Order leaves column B blank), so we
// match on the label rather than assuming a fixed column.
export function itemNumberIndex(headers) {
  return findHeaderIndex(headers, [
    /^item\s*(#|no\.?|num(ber)?)$/i,
    /^itemnumber$/i,
    /item\s*(#|number|num)\b/i,
    /\bitem\b/i,
  ]);
}

// Locate a description/name column by header name.
export function descriptionIndex(headers) {
  return findHeaderIndex(headers, [/descrip/i, /\bdesc\b/i, /item\s*name/i, /\bname\b/i]);
}

/*
  Locate the quantity column by header name.

  **Added 8 September 2026, because a replenishment run threw its lines away and
  the quantity with them.** The Transfer Order's own header is `QuantityOrdered`,
  and the two other patterns are there because a run is read from whatever the
  system exported that morning rather than from a fixed layout.

  **`QuantityReceived` must never match this.** It sits two columns from
  `QuantityOrdered` in the same file and is what has already arrived, so reading
  it as the amount to order would quietly halve, or zero, an order. The first
  pattern is anchored for exactly that reason and `\bqty\b` is the loosest that
  is allowed.
*/
export function quantityIndex(headers) {
  return findHeaderIndex(headers, [
    /^quantity\s*ordered$/i,
    /^qty\s*ordered$/i,
    /^quantity$/i,
    /^qty$/i,
  ]);
}

// 1-based column index -> spreadsheet letter (1->A, 27->AA) for display.
export function colLetter(n) {
  let s = '';
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

// Pull the header row + data rows out of a raw matrix (array of string arrays),
// following a documented layout. Within the copied column span we skip any
// blank rows that sit between the header and the first real data row, then stop
// at the first fully-blank row after data begins (the end of the data block).
// Keying off "the whole row is empty" rather than a single column is important:
// the ClaimSoft Transfer Order export leaves column B blank, so the old
// column-B end-marker stopped before the first data row.
export function extractTable(matrix, layout) {
  const h = layout.headerRow - 1;
  const headers = (matrix[h] || []).slice(layout.firstCol - 1, layout.lastCol).map(unwrapExcel);
  const rows = [];
  let started = false;
  for (let i = layout.firstDataRow - 1; i < matrix.length; i++) {
    const cells = (matrix[i] || []).slice(layout.firstCol - 1, layout.lastCol).map(unwrapExcel);
    const isEmpty = cells.every(c => c === '');
    if (isEmpty) {
      if (started) break; // reached the end of the data block
      continue;           // skip blank rows before the data starts
    }
    started = true;
    rows.push(cells);
  }
  return { headers, rows };
}

// XLSX (Excess Calc) -> matrix of formatted-text cells. raw:false keeps Excel's
// displayed value so we don't re-introduce float rounding on numeric cells.
export function xlsxToMatrix(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '', blankrows: true });
}

// CSV (Stock Status, Transfer Order) -> matrix. Decodes UTF-16/Win-1252 like the
// catalog importer, and keeps empty lines so documented row offsets stay aligned.
export function csvToMatrix(arrayBuffer) {
  const text = decodeCatalogBytes(arrayBuffer);
  const out = Papa.parse(text, { header: false, skipEmptyLines: false, dynamicTyping: false });
  return out.data;
}

// Parse one uploaded file against its layout. Returns { headers, rows, rowCount }.
export function parseSource(key, arrayBuffer) {
  const layout = LAYOUTS[key];
  if (!layout) throw new Error('Unknown source: ' + key);
  const matrix = layout.kind === 'xlsx' ? xlsxToMatrix(arrayBuffer) : csvToMatrix(arrayBuffer);
  const { headers, rows } = extractTable(matrix, layout);
  return { headers, rows, rowCount: rows.length };
}

// The exact 28-column ClaimSoft Transfer Order export header, in order. Used to
// build a Transfer Order file from standardized line items (e.g. a client order)
// so it drops straight into the replenishment flow.
export const TRANSFER_ORDER_HEADERS = [
  'TONumber', 'NewTONumber', 'PONumber', 'Status', 'SourceWarehouse', 'DestinationWarehouse',
  'Notes', 'CreatedBy', 'CreatedDate', 'TOModifiedBy', 'TOModifiedDate', 'DateReleased',
  'DateSent', 'DateClosed', 'ItemNumber', 'SerialNumber', 'Location', 'Bin', 'Description',
  'Vendor', 'QuantityOrdered', 'QuantityReceived', 'ModifiedBy', 'ModifiedDate', 'ReceivedBy',
  'DateReceived', 'ConvertToPO', 'POQuantity',
];

// Build a Transfer Order CSV (the format above) from line items
// [{ item_number, description, vendor, quantity }]. meta fills the order-level
// columns (Status defaults to 'Pending', QuantityReceived to '0'). Item numbers
// with a leading zero are wrapped as ="0123" exactly like the ClaimSoft export,
// so Excel/the replenishment parser keep the leading zero.
export function buildTransferOrderCsv(lines, meta = {}) {
  const esc = (v) => '"' + String(v ?? '').replace(/"/g, '""') + '"';
  const itemCell = (n) => {
    const s = String(n ?? '').trim();
    return /^0\d/.test(s) ? `="${s}"` : s; // mirror ClaimSoft's leading-zero wrapping
  };
  const rowFor = (l) => {
    const o = Object.fromEntries(TRANSFER_ORDER_HEADERS.map(h => [h, '']));
    o.Status = meta.status || '';
    o.SourceWarehouse = meta.sourceWarehouse || '';
    o.DestinationWarehouse = meta.destinationWarehouse || '';
    o.Notes = meta.notes || '';
    o.CreatedBy = meta.createdBy || '';
    o.CreatedDate = meta.createdDate || '';
    o.ItemNumber = itemCell(l.item_number);
    o.Description = l.description || '';
    o.Vendor = l.vendor || '';
    o.QuantityOrdered = l.quantity == null || l.quantity === '' ? '' : l.quantity;
    o.QuantityReceived = '0';
    // ItemNumber is pre-wrapped (may contain ="); don't double-quote that one.
    return TRANSFER_ORDER_HEADERS.map(h => (h === 'ItemNumber' ? o[h] : esc(o[h]))).join(',');
  };
  return TRANSFER_ORDER_HEADERS.map(esc).join(',') + '\n' + (lines || []).map(rowFor).join('\n') + '\n';
}

// Transfer Order filename -> office prefix + run date.
// Pattern (doc Step 1.3): (OFFICE)(MMDDYYYY)START, e.g. DFW05272026START.
/*
  The office and the date, read off the Transfer Order's own filename.

  **Two shapes, not one**, corrected 8 September 2026. It accepted
  `LOU09012026START.csv` and nothing else, and Group G's approved drawing of
  this screen shows the file called `(LOU)(09012026)START.csv`, with the
  brackets actually in the name. The app's own hint said "(OFFICE)(MMDDYYYY)START",
  which is where the drawing got them from: it was written with the brackets
  meaning "put the office here", and it reads as though they are part of the
  name.

  **Nobody could tell from the screen which was meant.** A file named the
  drawing's way had its office and date silently ignored, and the run then said
  the file "is not named the usual way" about a name that is exactly the one on
  the screen behind it. So both are read, and any brackets, spaces, dashes or
  underscores between the parts are ignored.
*/
export function parseTransferOrderFilename(name) {
  const base = (name || '').replace(/\.[^.]+$/, '');
  // Strip the separators a person or an export might put between the parts.
  const bare = base.replace(/[()[\]{}\s_-]/g, '');
  const m = bare.match(/^([A-Za-z]+)(\d{2})(\d{2})(\d{4})START$/i);
  if (!m) return { office: null, runDate: null, ok: false, raw: base };
  const [, office, mm, dd, yyyy] = m;
  return { office: office.toUpperCase(), runDate: `${yyyy}-${mm}-${dd}`, ok: true, raw: base };
}

// Validate a parsed run. priorHeaders is { [key]: string[] } from the most
// recent saved run, used to catch ClaimSoft/AccountEdge silently renaming a
// column (the silent-breakage risk flagged in Step 5 of the doc).
export function validateRun({ parsed, fileNames, office, runDate, priorHeaders }) {
  const errors = [];
  const warnings = [];

  for (const key of Object.keys(LAYOUTS)) {
    const layout = LAYOUTS[key];
    const p = parsed[key];
    if (!p) { errors.push(`${layout.label}: file not uploaded.`); continue; }
    if (p.rowCount === 0) errors.push(`${layout.label} has no rows under its headings.`);
    if (p.headers.length < layout.expectedCols) {
      warnings.push(`${layout.label} has ${p.headers.length} columns and should have ${layout.expectedCols}, `
        + `${colLetter(layout.firstCol)} to ${colLetter(layout.lastCol)}. It may have been exported differently.`);
    }
    // Header drift vs. the previous saved run.
    const prev = priorHeaders && priorHeaders[key];
    if (prev && prev.length) {
      const changed = prev.length !== p.headers.length ||
        prev.some((h, i) => (h || '').trim() !== (p.headers[i] || '').trim());
      if (changed) {
        warnings.push(`${layout.label} has different column headings from the last run. `
          + 'Check it was exported the same way before trusting what this run finds.');
      }
    }
  }

  if (!office || !runDate) {
    warnings.push('The Transfer Order file is not named the usual way'
      + (fileNames?.transfer_order ? ` ("${fileNames.transfer_order}")` : '')
      + ', so the office and the date could not be read off it. Fill them in below.');
  }

  return { errors, warnings, ok: errors.length === 0 };
}

// Compact header snapshot for persisting on the run (metadata.files[key].headers).
export function buildMetadata({ parsed, fileNames, office, runDate, validation }) {
  const files = {};
  for (const key of Object.keys(LAYOUTS)) {
    const p = parsed[key];
    files[key] = {
      filename: fileNames?.[key] || null,
      rows: p ? p.rowCount : 0,
      headers: p ? p.headers : [],
    };
  }
  return {
    office: office || null,
    run_date: runDate || null,
    files,
    validation: { errors: validation.errors, warnings: validation.warnings },
    spec_version: 1,
  };
}
